import type { AppState } from 'types/app';
import { isSupportedMobileDevice } from 'helpers/UserAgentUtils';
import { selectUnleashToggle } from 'selectors/features';
import { FEATURE_ZCS_BANNER_CATALOG_CACHE, FEATURE_ZCS_PTC_BASIC, FEATURE_ZCS_PTC_CATALOG } from 'constants/features';
import {
  ZCS_PTC_BUSINESS_UNIT_HEADER,
  ZCS_PTC_CATEGORY1_HEADER,
  ZCS_PTC_PLATFORM_DESKTOP,
  ZCS_PTC_PLATFORM_HEADER,
  ZCS_PTC_PLATFORM_MOBILE_WEB,
  ZCS_PTC_REFERRER_HEADER
} from 'constants/ptc';

function isFeatureEnabled(featureName: string, state: AppState): boolean {
  return Boolean(selectUnleashToggle(featureName, state)?.enabled);
}

/**
 * Maps Marty gender facet values (title-case) to PTC business unit.
 * Facet values: "Women", "Men", "Boys", "Girls", "Unisex"
 */
export function mapGenderToBusinessUnit(gender: string | null | undefined): string | undefined {
  if (!gender) return undefined;
  switch (gender.toLowerCase()) {
    case 'men':
      return 'mens';
    case 'women':
      return 'womens';
    case 'unisex':
      return 'unisex';
    case 'boys':
      return 'boys';
    case 'girls':
      return 'girls';
    default:
      return undefined;
  }
}

/**
 * Maps zc1 facet value (title-case) to PTC category1.
 * Returns undefined for unmapped types like "Gift Cards".
 */
export function mapTypeToCategory1(type: string | null | undefined): string | undefined {
  if (!type) return undefined;
  const lower = type.toLowerCase();
  switch (lower) {
    case 'shoes':
    case 'clothing':
    case 'accessories':
    case 'watches':
    case 'home':
    case 'bags':
    case 'jewelry':
    case 'eyewear':
    case 'beauty':
    case 'electronics':
    case 'sporting goods':
    case 'baby essentials':
      return lower;
    default:
      return undefined;
  }
}

export function buildPtcBasicHeaders(state: AppState): Record<string, string> {
  if (!isFeatureEnabled(FEATURE_ZCS_PTC_BASIC, state)) return {};

  // Preserve the existing viewport-based behavior for non-banner callers.
  const isMobile = state.headerFooter?.isMobile;
  const result: Record<string, string> = {
    [ZCS_PTC_PLATFORM_HEADER]: isMobile ? ZCS_PTC_PLATFORM_MOBILE_WEB : ZCS_PTC_PLATFORM_DESKTOP
  };

  const referrer = state.client?.request?.clientReferer?.trim();
  if (referrer) {
    result[ZCS_PTC_REFERRER_HEADER] = referrer;
  }

  return result;
}

interface CatalogContext {
  gender: string | null | undefined;
  productType: string | null | undefined;
  category: string | null | undefined;
}

export function buildPtcCatalogHeaders(state: AppState, catalogContext: CatalogContext): Record<string, string> {
  if (!isFeatureEnabled(FEATURE_ZCS_PTC_CATALOG, state)) return {};

  const { gender, productType, category } = catalogContext;
  const result: Record<string, string> = {};

  const businessUnit = mapGenderToBusinessUnit(gender);
  if (businessUnit) result['zappos-business-unit'] = businessUnit;

  const category1 = mapTypeToCategory1(productType);
  if (category1) result['zappos-category1'] = category1;

  if (category) result['zappos-category2'] = category.toLowerCase();

  return result;
}

export function buildAllPtcHeaders(state: AppState, catalogContext?: CatalogContext): Record<string, string> {
  const basic = buildPtcBasicHeaders(state);
  if (!catalogContext) return basic;
  const catalog = buildPtcCatalogHeaders(state, catalogContext);
  return { ...basic, ...catalog };
}

// The banner flag (on/off, no variants) selects delivery; the PTC flags independently enable its targeting dimensions.

export function isBannerCatalogCacheEnabled(state: AppState): boolean {
  return Boolean(selectUnleashToggle(FEATURE_ZCS_BANNER_CATALOG_CACHE, state)?.enabled);
}

export function isBannerPtcCatalogEnabled(state: AppState): boolean {
  return isBannerCatalogCacheEnabled(state) && isFeatureEnabled(FEATURE_ZCS_PTC_CATALOG, state);
}

// Only a single selected facet value is targetable.
function singleFacetValue(values: string[] | undefined): string | undefined {
  return values?.length === 1 ? values[0] : undefined;
}

/**
 * Builds bounded BU and Cat1 headers from resolved search facets.
 * The cache boundary validates both values again before forwarding.
 */
export function buildPtcBannerCatalogHeaders(state: AppState): Record<string, string> {
  if (!isBannerPtcCatalogEnabled(state)) return {};

  const singleSelects = state.filters?.selected?.singleSelects;
  const result: Record<string, string> = {};

  const businessUnit = mapGenderToBusinessUnit(singleFacetValue(singleSelects?.['txAttrFacet_Gender']));
  if (businessUnit) result[ZCS_PTC_BUSINESS_UNIT_HEADER] = businessUnit;

  const category1 = mapTypeToCategory1(singleFacetValue(singleSelects?.['zc1']));
  if (category1) result[ZCS_PTC_CATEGORY1_HEADER] = category1;

  return result;
}

/**
 * Builds only enabled banner PTC dimensions. Referrer normalization happens at the cache boundary.
 */
export function buildBannerPtcHeaders(state: AppState, { includeCatalog = true }: { includeCatalog?: boolean } = {}): Record<string, string> {
  if (!isBannerCatalogCacheEnabled(state)) return {};

  const result: Record<string, string> = {};
  if (isFeatureEnabled(FEATURE_ZCS_PTC_BASIC, state)) {
    // isMobile is a client-side viewport breakpoint and is null during SSR, so fall back to the user-agent.
    const isMobile = state.headerFooter?.isMobile ?? isSupportedMobileDevice(state.client?.request?.clientUserAgent ?? '');
    result[ZCS_PTC_PLATFORM_HEADER] = isMobile ? ZCS_PTC_PLATFORM_MOBILE_WEB : ZCS_PTC_PLATFORM_DESKTOP;

    const referrer = state.client?.request?.clientReferer?.trim();
    if (referrer) {
      result[ZCS_PTC_REFERRER_HEADER] = referrer;
    }
  }

  // Prewarm omits catalog dimensions so it fills the generic fallback entry.
  return includeCatalog ? { ...result, ...buildPtcBannerCatalogHeaders(state) } : result;
}
